/**
 * La primera pantalla del médico.
 *
 * Arriba, lo único que de verdad importa: cuánto debe. Después, accesos rápidos
 * y el estado del servicio. Nada de tablas: esto se lee de pie.
 *
 * En escritorio se parte en dos: a la izquierda el saldo y los accesos (lo
 * que se hace), a la derecha los pendientes y el consultorio (lo que se
 * mira). En el teléfono el orden del DOM es el de siempre: saldo → accesos →
 * pendientes → consultorio.
 */

import { Link } from 'react-router-dom';
import { dateLabel, periodLabel, PAYMENT_STATUS_LABEL, PAYMENT_STATUS_TONE, today, type PaymentStatus } from '@cleanblue/shared';
import { useApi } from '@/lib/hooks';
import { Async, Badge, Empty, KeyValueRow, Money } from '@/components/ui';
import { Icon } from '@/components/common/NavIcons';

interface Home {
  outstanding: string;
  overdue_count: number;
  pending_charges: {
    id: string; due_minor: number; paid_minor: number; due_date: string;
    payment_status: PaymentStatus; period_start: string; unit_code: string;
    service_name_snapshot: string;
  }[];
  units: { id: string; unit_code: string; name: string | null; location_name: string | null }[];
  last_visit: { scheduled_date: string; status: string } | null;
}

export function PortalHome() {
  const query = useApi<Home>('/portal/home');

  return (
    <Async query={query}>
      {(data) => {
        const outstanding = Number(data.outstanding);
        return (
          <div className="portal-split msec--split">
            <div>
              <section className="msec">
                <div className="mcard mcard--hero">
                  <div className="mrow" style={{ borderBottom: 0, paddingTop: 0 }}>
                    <span className="mrow__l">Tu saldo</span>
                    {data.overdue_count > 0 && <Badge tone="danger">{data.overdue_count} vencido</Badge>}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Money value={data.outstanding} size="xl" />
                  </div>
                  {outstanding > 0 ? (
                    <Link to="/portal/cobros" className="btn btn--primary btn--block mt-2">
                      Ver y pagar
                    </Link>
                  ) : (
                    <p className="small muted mt-1">Estás al día. Gracias.</p>
                  )}
                </div>
              </section>

              <section className="msec">
                <div className="msec__title">Accesos</div>
                {/* Íconos de la familia, no glifos: «!», «₡», «★» y «⋯» — la
                    estrella salía como emoji dorado en el teléfono. */}
                <div className="quick">
                  <Link to="/portal/servicios" className="is-urgent">
                    <span className="quick__ico"><Icon name="incidencias" size={20} /></span>
                    Pedir atención
                  </Link>
                  <Link to="/portal/cobros">
                    <span className="quick__ico"><Icon name="cobros" size={20} /></span>
                    Mis cobros
                  </Link>
                  <Link to="/portal/mejora">
                    <span className="quick__ico"><Icon name="mejora" size={20} /></span>
                    Reportar algo
                  </Link>
                  <Link to="/portal/mas">
                    <span className="quick__ico"><Icon name="mas" size={20} /></span>
                    Mi cuenta
                  </Link>
                </div>
              </section>
            </div>

            <aside className="msec--split-lateral">
              {data.pending_charges.length > 0 && (
                <section className="msec">
                  <div className="msec__title">Pendientes de pago</div>
                  {data.pending_charges.map((c) => (
                    <Link key={c.id} to={`/portal/cobros/${c.id}`} className="mcard mcard--par" style={{ display: 'block' }}>
                      <div className="mrow" style={{ paddingTop: 0 }}>
                        <span>
                          <strong>{periodLabel(c.period_start)}</strong>
                          <span className="mhead__sub" style={{ display: 'block', color: 'var(--muted)' }}>
                            {c.unit_code} · {c.service_name_snapshot}
                          </span>
                        </span>
                        <span className="mrow__r"><Money value={c.due_minor - c.paid_minor} /></span>
                      </div>
                      <div className="mrow" style={{ borderBottom: 0, paddingBottom: 0 }}>
                        <span className="mrow__l">
                          Vence el {dateLabel(c.due_date)}
                          {c.due_date < today() && ' · vencido'}
                        </span>
                        <Badge tone={PAYMENT_STATUS_TONE[c.payment_status]}>
                          {PAYMENT_STATUS_LABEL[c.payment_status]}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </section>
              )}

              <section className="msec">
                <div className="msec__title">Tu consultorio</div>
                {data.units.length === 0 ? (
                  <Empty icon="consultorio" title="Todavía no estás asociado a un consultorio" />
                ) : (
                  data.units.map((u) => (
                    <div key={u.id} className="mcard mcard--par">
                      <KeyValueRow label="Consultorio" style={{ paddingTop: 0 }}>{u.unit_code}</KeyValueRow>
                      {u.location_name && (
                        <KeyValueRow label="Ubicación">{u.location_name}</KeyValueRow>
                      )}
                      {/* La limpieza es DIARIA: mostrar «última limpieza» con
                          fecha sugería un servicio esporádico (dueño,
                          29-ago-2026). Se dice la cadencia, que es lo cierto. */}
                      <KeyValueRow label="Limpieza" style={{ borderBottom: 0 }}>Todos los días, lunes a viernes</KeyValueRow>
                    </div>
                  ))
                )}
              </section>
            </aside>
          </div>
        );
      }}
    </Async>
  );
}
